import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Tooltip,
    IconButton,
    Portal,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Box,
    Avatar,
} from '@mui/material';
import Draggable from 'react-draggable';
import { styled } from '@mui/system';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import ClientItemsService from '../../../Services/clientItems.service';
import ClientItemPricesEditor, { arePricesValid } from './ClientItemPricesEditor';
import { normalizePrices, getDefaultPriceAmount, pricesToApiDto } from '../../../helpers/clientItemPrices';

const StyledContainer = styled('div')({
    position: 'absolute',
    top: '10%',
    left: '20%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    minHeight: '30vh',
    maxHeight: '80vh',
    overflowY: 'auto',
    minWidth: '500px',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
    border: '1px solid #304fff',
});

function ClientItemDraggableDialog({ open, onClose, data, onSave, onImageRemoved, caseId, token, direction, t }) {
    const [formData, setFormData] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [hasServerImage, setHasServerImage] = useState(false);
    const [removingImage, setRemovingImage] = useState(false);
    const nodeRef = useRef(null);

    useEffect(() => {
        if (data) {
            const prices = normalizePrices(data.prices ?? data.Prices, data.price ?? data.Price);
            const formDataWithVat = { 
                ...data, 
                includeVat: data.includeVat === true ? true : false,
                prices,
                price: getDefaultPriceAmount(prices, data.price ?? data.Price) ?? '',
            };
            setFormData(formDataWithVat);
            setHasServerImage(!!data.imageUrl);
            setImagePreview(data.imageUrl || null);
        }
    }, [data]);

    const handleChange = (field, value) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'prices') {
                next.price = getDefaultPriceAmount(value, '') ?? '';
            }
            return next;
        });
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setFormData((prev) => ({ ...prev, imageFile: file }));
            };
            reader.readAsDataURL(file);
        }
        event.target.value = '';
    };

    const performRemoveImage = async () => {
        const itemCaseId = caseId ?? formData.caseId ?? formData.CaseId;
        const originalMakat = data?.makat ?? data?.Makat ?? formData.makat;
        if (!itemCaseId || !originalMakat || !token) return;

        setRemovingImage(true);
        try {
            const resp = await ClientItemsService.UpdateClientItem(
                itemCaseId,
                originalMakat,
                formData.makat,
                formData.name,
                getDefaultPriceAmount(formData.prices, formData.price) || 0,
                formData.includeVat === true,
                null,
                token,
                true,
                pricesToApiDto(formData.prices)
            );
            if (!resp.ok) {
                const errorTxt = await resp.text();
                toast.error(`${t('FailedToRemoveImage')}: ${errorTxt}`);
                return;
            }

            setImagePreview(null);
            setHasServerImage(false);
            setFormData((prev) => {
                const { imageFile, ...rest } = prev;
                return { ...rest, imageUrl: null };
            });

            const itemId = formData.id ?? formData.Id;
            if (onImageRemoved && itemId) {
                await onImageRemoved(itemId);
            }
        } catch (err) {
            console.error(err);
            toast.error(t('FailedToRemoveImage'));
        } finally {
            setRemovingImage(false);
        }
    };

    const handleRemovePickedImage = () => {
        setFormData((prev) => {
            const { imageFile, ...rest } = prev;
            return rest;
        });

        if (hasServerImage && (formData.imageUrl || data?.imageUrl)) {
            setImagePreview(formData.imageUrl || data.imageUrl);
        } else {
            setImagePreview(null);
        }
    };

    const handleRemoveImage = async () => {
        if (formData.imageFile) {
            handleRemovePickedImage();
            return;
        }

        const result = await Swal.fire({
            title: t('ConfirmRemoveImage'),
            text: t('AreYouSureRemoveClientItemImage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('YesDelete'),
            cancelButtonText: t('Cancel'),
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;
        await performRemoveImage();
    };

    const showRemoveImageButton = !!imagePreview;

    const isFormValid = () => {
        return formData.makat && formData.makat.trim() !== '' && 
               formData.name && formData.name.trim() !== '' && 
               arePricesValid(formData.prices);
    };

    const handleSave = () => {
        if (isFormValid()) {
            onSave(formData);
        }
    };

    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '48px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
        },
    };

    if (!open) return null;

    return (
        <Portal>
            <Draggable handle=".draggable-handle" nodeRef={nodeRef}>
                <StyledContainer ref={nodeRef} style={{ direction }}>
                    {/* Header */}
                    <div
                        className="draggable-handle"
                        style={{
                            cursor: 'move',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Tooltip title={t('Exit')}>
                            <IconButton onClick={onClose}>
                                <CancelIcon style={{ color: '#304FFF' }} />
                            </IconButton>
                        </Tooltip>
                        <div>{t('EditClientItem')}: {formData.makat}</div>
                        <div />
                    </div>

                    {/* Form Fields */}
                    <div className="form-row" style={{ marginBottom: 16 }}>
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title" style={{ marginBottom: 4 }}>
                                {t('Makat')}
                            </div>
                            <TextField
                                fullWidth
                                value={formData.makat || ''}
                                onChange={(e) => handleChange('makat', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                                required
                                error={!formData.makat || formData.makat.trim() === ''}
                                helperText={(!formData.makat || formData.makat.trim() === '') ? t('Required') : ''}
                            />
                        </div>
                    </div>

                    <div className="form-row" style={{ marginBottom: 16 }}>
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title" style={{ marginBottom: 4 }}>
                                {t('Name')}
                            </div>
                            <TextField
                                fullWidth
                                value={formData.name || ''}
                                onChange={(e) => handleChange('name', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                                required
                                error={!formData.name || formData.name.trim() === ''}
                                helperText={(!formData.name || formData.name.trim() === '') ? t('Required') : ''}
                            />
                        </div>
                    </div>

                    <div className="form-row" style={{ marginBottom: 16 }}>
                        <div className="edit-form-row-item">
                            <ClientItemPricesEditor
                                value={formData.prices}
                                onChange={(prices) => handleChange('prices', prices)}
                                direction={direction}
                                inputPropsStyle={inputPropsStyle}
                                inputStyle={inputStyle}
                            />
                        </div>
                    </div>

                    <div className="form-row" style={{ marginBottom: 16 }}>
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title" style={{ marginBottom: 4 }}>
                                {t('vat')}
                            </div>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel id="vat-label">{t('vat')}</InputLabel>
                                <Select
                                    labelId="vat-label"
                                    value={formData.includeVat ? 'ItemWithVat' : 'ItemWithoutVat'}
                                    onChange={(e) => handleChange('includeVat', e.target.value === 'ItemWithVat')}
                                    label={t('vat')}
                                    sx={{
                                        height: '48px',
                                        padding: '0 10px',
                                        borderRadius: '8px',
                                        background: '#F5F5F6',
                                        border: '1px solid transparent',
                                        '&:focus-within': {
                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                        },
                                        '& .MuiSelect-select': {
                                            padding: '12px 10px',
                                            textAlign: direction === 'rtl' ? 'right' : 'left',
                                            direction: direction === 'rtl' ? 'rtl' : 'ltr',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#666',
                                            fontSize: '14px',
                                        },
                                    }}
                                >
                                    <MenuItem value="ItemWithVat">{t('ItemWithVat')}</MenuItem>
                                    <MenuItem value="ItemWithoutVat">{t('ItemWithoutVat')}</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>

                    {/* Image Upload */}
                    <div className="form-row" style={{ marginBottom: 16 }}>
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title" style={{ marginBottom: 8 }}>
                                {t('ItemImage')}
                            </div>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {imagePreview && (
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <Avatar
                                            src={imagePreview}
                                            variant="rounded"
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                opacity: removingImage ? 0.5 : 1,
                                            }}
                                        />
                                        {showRemoveImageButton && (
                                            <Tooltip title={t('RemoveImage')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleRemoveImage}
                                                    disabled={removingImage}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -8,
                                                        right: -8,
                                                        width: 24,
                                                        height: 24,
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #ccc',
                                                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                                        '&:hover': {
                                                            backgroundColor: '#fee',
                                                        },
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="image-upload-edit"
                                    type="file"
                                    onChange={handleImageChange}
                                    disabled={removingImage}
                                />
                                <label htmlFor="image-upload-edit">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        disabled={removingImage}
                                        startIcon={<PhotoCamera />}
                                        sx={{
                                            borderColor: '#304FFF',
                                            color: '#304FFF',
                                            '&:hover': {
                                                borderColor: '#1E40FF',
                                                backgroundColor: 'rgba(48, 79, 255, 0.04)',
                                            },
                                        }}
                                    >
                                        {t('UploadImage')}
                                    </Button>
                                </label>
                            </Box>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '16px', gap: '10px' }}>
                        <div onClick={onClose} className="edit-cancel-button">
                            {t('Cancel')}
                        </div>
                        <div 
                            onClick={handleSave} 
                            className={`edit-save-button ${!isFormValid() ? 'disabled' : ''}`}
                            style={{
                                opacity: !isFormValid() ? 0.5 : 1,
                                cursor: !isFormValid() ? 'not-allowed' : 'pointer',
                                backgroundColor: !isFormValid() ? '#ccc' : undefined
                            }}
                        >
                            {t('Save')}
                        </div>
                    </div>
                </StyledContainer>
            </Draggable>
        </Portal>
    );
}

export default ClientItemDraggableDialog;
